import { writable, derived, get } from 'svelte/store'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

export type EditorViewMode = 'source' | 'wysiwyg'

interface EditorViewState {
  mode: EditorViewMode
  enableMarkdown: boolean
  markdownContent: string
  tabModes: Map<string, EditorViewMode> // Store mode per tab
}

// Don't customize the renderer - let marked handle it
// We'll fix spacing issues with CSS instead

// Configure marked options for consistent rendering
marked.setOptions({
  breaks: false,  // Don't convert single newlines to <br>
  gfm: true,
  mangle: false,
  pedantic: false,
  smartLists: true,
  smartypants: false
} as any)

// Undo/Redo history management
interface HistoryEntry {
  content: string
  cursorPosition?: number
  timestamp: number
}

const MAX_HISTORY_SIZE = 100
let history: HistoryEntry[] = []
let historyIndex = -1

export function pushToHistory(content: string, cursorPosition?: number) {
  // Remove any entries after current index
  history = history.slice(0, historyIndex + 1)
  
  // Add new entry
  history.push({
    content,
    cursorPosition,
    timestamp: Date.now()
  })
  
  // Limit history size
  if (history.length > MAX_HISTORY_SIZE) {
    history = history.slice(-MAX_HISTORY_SIZE)
  }
  
  historyIndex = history.length - 1
}

export function undo(): HistoryEntry | null {
  if (historyIndex > 0) {
    historyIndex--
    return history[historyIndex]
  }
  return null
}

export function redo(): HistoryEntry | null {
  if (historyIndex < history.length - 1) {
    historyIndex++
    return history[historyIndex]
  }
  return null
}

export function clearHistory() {
  history = []
  historyIndex = -1
}

// Load saved mode preference from localStorage
const loadSavedMode = (): EditorViewMode => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('editorViewMode')
    if (saved === 'source' || saved === 'wysiwyg') {
      return saved
    }
  }
  return 'wysiwyg' // Default to WYSIWYG (reading mode)
}

// Load saved tab modes from localStorage
const loadTabModes = (): Map<string, EditorViewMode> => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('editorTabModes')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return new Map(Object.entries(parsed))
      } catch (e) {
        console.error('Failed to parse saved tab modes:', e)
      }
    }
  }
  return new Map()
}

// Save mode preference to localStorage
const saveModePreference = (mode: EditorViewMode) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('editorViewMode', mode)
  }
}

// Save tab modes to localStorage
const saveTabModes = (tabModes: Map<string, EditorViewMode>) => {
  if (typeof window !== 'undefined') {
    const obj = Object.fromEntries(tabModes)
    localStorage.setItem('editorTabModes', JSON.stringify(obj))
  }
}

// Create the store with default values
const createEditorViewStore = () => {
  const { subscribe, set, update } = writable<EditorViewState>({
    mode: loadSavedMode(),
    enableMarkdown: false,
    markdownContent: '',
    tabModes: loadTabModes()
  })
  
  let currentTabId: string | null = null

  return {
    subscribe,
    setMode: (mode: EditorViewMode) => {
      update(state => {
        // Save mode for current tab
        if (currentTabId) {
          state.tabModes.set(currentTabId, mode)
        }
        
        // Save to localStorage
        saveModePreference(mode)
        const newTabModes = new Map(state.tabModes)
        saveTabModes(newTabModes)
        
        return {
          ...state,
          mode,
          tabModes: newTabModes
        }
      })
    },
    toggleMode: () => {
      update(state => {
        // Toggle between: source <-> wysiwyg
        const newMode: EditorViewMode = state.mode === 'source' ? 'wysiwyg' : 'source'
        
        // Save mode for current tab
        if (currentTabId) {
          state.tabModes.set(currentTabId, newMode)
        }
        
        // Save to localStorage
        saveModePreference(newMode)
        const newTabModes = new Map(state.tabModes)
        saveTabModes(newTabModes)
        
        return {
          ...state,
          mode: newMode,
          tabModes: newTabModes
        }
      })
    },
    setActiveTab: (tabId: string) => {
      currentTabId = tabId
      update(state => {
        // Restore mode for this tab or use saved default
        const savedMode = state.tabModes.get(tabId) || loadSavedMode()
        return {
          ...state,
          mode: savedMode
        }
      })
    },
    setMarkdownEnabled: (enabled: boolean) => {
      update(state => ({
        ...state,
        enableMarkdown: enabled
      }))
    },
    setMarkdownContent: (content: string, addToHistory = true) => {
      if (addToHistory) {
        pushToHistory(content)
      }
      update(state => ({
        ...state,
        markdownContent: content
      }))
    },
    reset: () => {
      clearHistory()
      set({
        mode: loadSavedMode(),
        enableMarkdown: false,
        markdownContent: '',
        tabModes: new Map()
      })
      currentTabId = null
      // Clear saved tab modes but keep default mode preference
      saveTabModes(new Map())
    }
  }
}

// Export singleton instance
export const editorViewStore = createEditorViewStore()

// Derived store for quick access to current mode
export const currentViewMode = derived(
  editorViewStore,
  $store => $store.mode
)

// Derived store for markdown enabled state
export const isMarkdownEnabled = derived(
  editorViewStore,
  $store => $store.enableMarkdown
)

// HTML content derived from markdown
export const htmlContent = derived(
  editorViewStore,
  $store => {
    if (!$store.markdownContent) return ''

    // Use marked.parse() instead of parseInline() to support block elements like images
    let rawHtml = marked.parse($store.markdownContent)
    
    // Post-processing to clean up list rendering
    // Remove p tags from list items only
    if (typeof rawHtml === 'string') {
      rawHtml = rawHtml.replace(/<li>([\s\S]*?)<\/li>/gi, (match, content) => {
        // Remove all p tags but keep their content
        const cleanContent = content
          .replace(/<p>/gi, '')
          .replace(/<\/p>/gi, '')
          .trim()
        return `<li>${cleanContent}</li>`
      })
    }
    
    // Sanitize HTML to prevent XSS
    return DOMPurify.sanitize(rawHtml as string, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'strong', 'b', 'em', 'i', 'code', 'pre',
        'blockquote', 'ol', 'ul', 'li',
        'a', 'img',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'del', 'ins', 'sup', 'sub',
        'span', 'div', 'input'
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel', 'type', 'checked', 'disabled']
    })
  }
)

// Helper to convert HTML back to markdown (for WYSIWYG mode)
export function htmlToMarkdown(html: string): string {
  // Create a temporary div to parse HTML
  const div = document.createElement('div')
  div.innerHTML = DOMPurify.sanitize(html)
  
  let markdown = ''
  
  function processNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || ''
    }
    
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement
      const tagName = element.tagName.toLowerCase()
      const children = Array.from(node.childNodes).map(processNode).join('')
      
      switch (tagName) {
        case 'h1': return `# ${children}\n\n`
        case 'h2': return `## ${children}\n\n`
        case 'h3': return `### ${children}\n\n`
        case 'h4': return `#### ${children}\n\n`
        case 'h5': return `##### ${children}\n\n`
        case 'h6': return `###### ${children}\n\n`
        case 'p': {
          // Check if we're inside a list item
          const parent = element.parentElement
          if (parent && parent.tagName.toLowerCase() === 'li') {
            return children // No extra newlines for p inside li
          }
          return `${children}\n\n`
        }
        case 'br': return '\n'
        case 'hr': return '---\n\n'
        case 'strong':
        case 'b': return `**${children}**`
        case 'em':
        case 'i': return `*${children}*`
        case 'code': {
          // Check if it's inside a pre tag
          if (element.parentElement?.tagName.toLowerCase() === 'pre') {
            return children
          }
          return `\`${children}\``
        }
        case 'pre': {
          const codeElement = element.querySelector('code')
          const code = codeElement ? codeElement.textContent : element.textContent
          return `\`\`\`\n${code}\n\`\`\`\n\n`
        }
        case 'blockquote': return `> ${children.split('\n').join('\n> ')}\n\n`
        case 'ul': return children
        case 'ol': return children
        case 'li': {
          const parent = element.parentElement
          // Process children to handle p tags properly
          let cleanChildren = children
          
          // If the content contains p tags, extract just the text
          if (element.querySelector('p')) {
            const texts: string[] = []
            element.childNodes.forEach(node => {
              if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent?.trim()
                if (text) texts.push(text)
              } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement
                if (el.tagName.toLowerCase() === 'p') {
                  const text = el.textContent?.trim()
                  if (text) texts.push(text)
                } else {
                  const text = processNode(node).trim()
                  if (text) texts.push(text)
                }
              }
            })
            cleanChildren = texts.join(' ')
          } else {
            cleanChildren = children.trim()
          }
          
          if (parent?.tagName.toLowerCase() === 'ul') {
            return `- ${cleanChildren}\n`
          } else if (parent?.tagName.toLowerCase() === 'ol') {
            const index = Array.from(parent.children).indexOf(element) + 1
            return `${index}. ${cleanChildren}\n`
          }
          return cleanChildren
        }
        case 'a': {
          const href = element.getAttribute('href') || ''
          const title = element.getAttribute('title')
          if (title) {
            return `[${children}](${href} "${title}")`
          }
          return `[${children}](${href})`
        }
        case 'img': {
          const src = element.getAttribute('src') || ''
          const alt = element.getAttribute('alt') || ''
          const title = element.getAttribute('title')
          if (title) {
            return `![${alt}](${src} "${title}")`
          }
          return `![${alt}](${src})`
        }
        case 'del': return `~~${children}~~`
        case 'ins': return `++${children}++`
        case 'sup': return `^${children}^`
        case 'sub': return `~${children}~`
        default: return children
      }
    }
    
    return ''
  }
  
  Array.from(div.childNodes).forEach(node => {
    markdown += processNode(node)
  })
  
  // Clean up extra newlines
  markdown = markdown.replace(/\n{3,}/g, '\n\n').trim()
  
  return markdown
}